import React, { useEffect, useRef } from 'react';
import { Box, MessageSquare, Pencil, UserRound, X } from 'lucide-react';

const FEATURES = [
  {
    icon: UserRound,
    title: 'Built for agencies',
    body: 'Deliver a premium client experience under your own brand.',
  },
  {
    icon: Pencil,
    title: 'Fully Customisable',
    body: 'Tailor workflows, branding, and customisation to suit your clients.',
  },
  {
    icon: MessageSquare,
    title: 'Real time collaboration',
    body: 'Feedback, approvals and personalised messaging in one place.',
  },
  {
    icon: Box,
    title: 'Seamless integration',
    body: 'Connects with dashboards, admin tools and workflow systems.',
  },
];

/**
 * Full-screen preview for the agency VELLO / creative collaboration portal thumbnail.
 * User-facing strings use British English spelling.
 */
export function ClientPortalExampleModal({ open, onClose }) {
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    const t = window.setTimeout(() => closeBtnRef.current?.focus(), 0);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(t);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100]" aria-hidden={!open}>
      <button
        type="button"
        className="absolute inset-0 bg-black/65 backdrop-blur-[2px]"
        aria-label="Close preview"
        onClick={onClose}
      />
      <div className="relative z-[101] flex max-h-[100dvh] min-h-full items-center justify-center overflow-y-auto p-3 sm:p-6">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="portal-example-title"
          className="portal-modal-panel relative my-auto rounded-2xl border border-neutral-200 bg-white px-4 py-6 shadow-2xl sm:px-8 sm:py-8"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            ref={closeBtnRef}
            type="button"
            className="absolute right-3 top-3 rounded-full border border-neutral-200 bg-white p-2 text-neutral-600 shadow-sm transition-colors hover:bg-neutral-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#cc9600] sm:right-4 sm:top-4"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-5 w-5" strokeWidth={1.75} aria-hidden />
          </button>

          <header className="pr-10 text-center sm:pr-12 sm:text-left">
            <h2 id="portal-example-title" className="sr-only">
              VELLO — Creative Collaboration Portal
            </h2>
            <img
              src="/assets/images/Portal_modal_logo.webp"
              alt=""
              width={400}
              height={80}
              className="mx-auto h-16 w-auto max-w-full object-contain object-center sm:mx-0 sm:h-20"
              loading="eager"
              decoding="async"
            />
            <p className="mx-auto mt-3 max-w-3xl text-base leading-relaxed text-neutral-600 sm:mx-0 sm:mt-4 sm:text-lg">
              A branded client portal hub with project timelines,
              <br />
              approvals, feedback, and real-time collaboration
            </p>
          </header>

          <div className="mt-8 overflow-hidden rounded-xl bg-white">
            <img
              src="/assets/images/Portal_modal_example.webp"
              alt="VELLO creative collaboration portal: desktop and mobile layouts"
              width={1200}
              height={800}
              className="h-auto w-full object-contain"
              loading="eager"
              decoding="async"
            />
          </div>

          <ul className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <li key={title} className="flex gap-3 text-left">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-neutral-200 bg-white text-neutral-700 shadow-sm">
                  <Icon className="h-8 w-8" strokeWidth={1.5} aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-base font-semibold text-neutral-900 sm:text-lg">{title}</p>
                  <p className="mt-1 text-base leading-relaxed text-neutral-600 sm:text-lg">{body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default ClientPortalExampleModal;
